import { Box, Transition } from '@mantine/core';
import React, { forwardRef } from 'react';
import { useInView, useReducedMotion, useStaggeredAnimation } from '../hooks';

export interface StaggeredListProps {
  children: React.ReactNode | React.ReactNode[];
  animation?: 'fade' | 'slide-up' | 'slide-left' | 'scale';
  duration?: number;
  baseDelay?: number;
  staggerDelay?: number;
  threshold?: number;
  triggerOnce?: boolean;
}

const StaggeredList = forwardRef<HTMLDivElement, StaggeredListProps>(
  (
    {
      children,
      animation = 'slide-up',
      duration = 300,
      baseDelay = 0,
      staggerDelay = 50,
      threshold = 0.1,
      triggerOnce = true,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const { ref: inViewRef, inView } = useInView({
      threshold,
      triggerOnce,
    });

    const childrenArray = React.Children.toArray(children);

    const staggeredItems = useStaggeredAnimation({
      count: childrenArray.length,
      baseDelay,
      staggerDelay,
      duration,
    });

    const getTransition = () => {
      switch (animation) {
        case 'slide-up':
          return {
            in: { opacity: 1, transform: 'translateY(0)' },
            out: { opacity: 0, transform: 'translateY(20px)' },
            transitionProperty: 'opacity, transform',
          };
        case 'slide-left':
          return {
            in: { opacity: 1, transform: 'translateX(0)' },
            out: { opacity: 0, transform: 'translateX(20px)' },
            transitionProperty: 'opacity, transform',
          };
        case 'scale':
          return {
            in: { opacity: 1, transform: 'scale(1)' },
            out: { opacity: 0, transform: 'scale(0.9)' },
            transitionProperty: 'opacity, transform',
          };
        case 'fade':
        default:
          return 'fade';
      }
    };

    return (
      <Box ref={inViewRef}>
        <Box ref={ref}>
          {childrenArray.map((child, index) => {
            const { delay, duration: itemDuration } = staggeredItems[index] || { delay: 0, duration };

            return (
              <Transition
                key={index}
                mounted={inView}
                transition={getTransition()}
                duration={reducedMotion ? 0 : itemDuration}
                timingFunction="ease-out"
              >
                {(styles) => (
                  <Box
                    style={{
                      ...styles,
                      transitionDelay: reducedMotion ? '0ms' : `${delay}ms`,
                    }}
                  >
                    {child}
                  </Box>
                )}
              </Transition>
            );
          })}
        </Box>
      </Box>
    );
  }
);

StaggeredList.displayName = 'StaggeredList';

export default StaggeredList;
